import React, { useRef, useState } from 'react';
import MemberRowActions from '../../../components/MemberRowActions/MemberRowActions';
import MemberLastCallCell from '../../../components/MemberLastCallCell/MemberLastCallCell';
import {
  formatDomainCategory,
  formatMemberAddress,
  formatStatsDisplayDate,
  memberEntityLabel,
} from '../../../utils/marketingApi';
import RefollowSourceBatchChip from './RefollowSourceBatchChip';
import CustomerCallHistorySplit from '../../../components/CustomerCallHistory/CustomerCallHistorySplit';
import cm from '../CallManager.module.scss';
import s from './Refollow.module.scss';

function fmtCell(v) {
  const t = (v ?? '').toString().trim();
  return t || '—';
}

function refollowDisplayName(row) {
  const business = (row?.business_name || '').trim();
  const person = (row?.person_name || '').trim();
  const combined = (row?.name || '').trim();
  if (business) return business;
  if (person && !['temporary user', 'n/a', 'na', 'unknown'].includes(person.toLowerCase())) {
    return person;
  }
  return combined;
}

function statusBadge(row, actionedView) {
  if (!actionedView) {
    const d = row.scheduled_date ? formatStatsDisplayDate(row.scheduled_date) : '—';
    return { label: `Due ${d}`, tone: 'followup' };
  }
  const st = String(row.status || '').toLowerCase();
  if (st === 'done') return { label: 'Done', tone: 'success' };
  if (st === 'cancelled') return { label: 'Cancelled', tone: 'danger' };
  return { label: st || 'Closed', tone: 'muted' };
}

export default function RefollowMemberRow({
  row,
  onOpenCustomer,
  onDone,
  onCancelled,
  onRefresh,
  onPatchRow,
  showToast,
  actionedView = false,
  recentActionKey = null,
  sourceBatchFilter = null,
  onSourceBatchFilterToggle = null,
}) {
  const [expanded, setExpanded] = useState(false);
  const lastContactsRef = useRef(null);
  const entity = memberEntityLabel(row);
  const member = {
    customer_id: row.customer_id,
    phone_e164: row.phone_e164,
    name: row.name,
    id: row.source_batch_member_id,
    batch_member_id: row.source_batch_member_id,
    outcome: 'follow_up',
    domain: row.domain,
    category: row.category,
    address: row.address,
    city: row.city,
    pincode: row.pincode,
    whatsapp_sent: row.whatsapp_sent,
    last_wa: row.last_wa,
  };
  const opsBatchId = row.source_batch_id ?? row.ops_batch_id ?? null;
  const badge = statusBadge(row, actionedView);
  const cardTone = actionedView
    ? (String(row.status || '').toLowerCase() === 'done'
      ? cm.memberCardToneSuccess
      : cm.memberCardToneDanger)
    : '';

  const remarksText = row.remarks?.trim() || '';
  const displayName = refollowDisplayName(row);

  return (
    <article
      className={`${cm.memberCard} ${s.refollowMemberCard}${cardTone ? ` ${cardTone}` : ''}${expanded ? ` ${s.memberExpanded}` : ''}${actionedView ? ` ${s.refollowMemberCardActioned}` : ''}${recentActionKey === row.id ? ` ${cm.memberCardFocus}` : ''}`}
      onClick={(e) => {
        if (actionedView) return;
        if (e.target.closest('[data-call-dropdown-panel]')) return;
        if (e.target.closest(`.${s.refollowLastContactsMeta}`)) return;
        setExpanded((v) => !v);
      }}
    >
      <div className={s.refollowCardRow}>
        <div className={`${cm.memberCardMain} ${s.refollowCardMain}`}>
          <div className={`${cm.memberCardTop} ${s.refollowCardTop}`}>
            <div className={cm.memberNameBlock}>
              <button
                type="button"
                className={`${cm.memberNameLink} ${s.refollowNameHeading}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenCustomer(row);
                }}
              >
                {fmtCell(displayName)}
              </button>
              <span
                className={`${cm.outcomeBadge} ${s.refollowStatusBadge}`}
                data-tone={badge.tone}
              >
                {badge.label}
              </span>
            </div>
          </div>
          <div className={s.refollowMetaGrid}>
            <div className={cm.metaItem}>
              <span className={cm.metaLabel}>Phone</span>
              <span className={cm.metaValue}>{fmtCell(row.phone_e164)}</span>
            </div>
            <div className={cm.metaItem}>
              <span className={cm.metaLabel}>ID</span>
              <span className={`${cm.metaValue} ${cm.entityBadge}`} data-type={entity.type}>{entity.label}</span>
            </div>
            {opsBatchId || row.source_batch_name ? (
              <div className={`${cm.metaItem} ${s.refollowOpsBatchMeta}`}>
                <span className={cm.metaLabel}>CM batch</span>
                <RefollowSourceBatchChip
                  opsBatchId={opsBatchId}
                  name={row.source_batch_name}
                  description={row.source_batch_description}
                  stacked={!onSourceBatchFilterToggle}
                  filterActive={sourceBatchFilter != null && Number(sourceBatchFilter) === Number(opsBatchId)}
                  onFilterToggle={onSourceBatchFilterToggle}
                />
              </div>
            ) : null}
            <div className={cm.metaItem}>
              <span className={cm.metaLabel}>Domain · Category</span>
              <span className={cm.metaValue}>{formatDomainCategory(row)}</span>
            </div>
            <div className={cm.metaItem}>
              <span className={cm.metaLabel}>Address · City · Pincode</span>
              <span className={cm.metaValue}>{formatMemberAddress(row)}</span>
            </div>
            <div
              className={`${cm.metaItem} ${cm.lastCalledMeta} ${cm.lastContactsHit} ${s.refollowLastContactsMeta}`}
              onClick={(e) => {
                e.stopPropagation();
                if (e.target.closest('[data-call-dropdown-panel]')) return;
                if (e.target.closest('[data-member-last-call]')) return;
                if (e.target.closest('button')) return;
                lastContactsRef.current?.toggleOpen();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  lastContactsRef.current?.toggleOpen();
                }
              }}
              role="presentation"
            >
              <span className={cm.metaLabel}>Last contacts</span>
              <MemberLastCallCell
                ref={lastContactsRef}
                phone={row.phone_e164}
                lastCall={row.last_manual_call ?? row.last_call}
                lastManualCall={row.last_manual_call ?? row.last_call}
                lastBotCall={row.last_bot_call ?? null}
                lastWa={row.last_wa ?? null}
                leadId={row.lead_id ?? null}
                customerId={row.customer_id}
                compact
                fixedContactRows
                serverPaginatedHistory
                delegateToggleToParent
                panelAlign="end"
              />
            </div>
          </div>
        </div>

        <aside
          className={s.refollowRemarksAside}
          onClick={(e) => e.stopPropagation()}
          aria-label="Customer remarks"
        >
          <span className={s.refollowRemarksAsideLabel}>Customer remarks</span>
          <p className={s.refollowRemarksAsideBody}>{remarksText || '—'}</p>
        </aside>

        {actionedView ? null : (
          <div className={s.refollowActions} onClick={(e) => e.stopPropagation()}>
            <div className={s.refollowActionsInner}>
            <MemberRowActions
              batchId={opsBatchId}
              member={member}
              refollowMemberId={row.id}
              marketingInitiated={false}
              showCall
              showCreateCustomer={false}
              showDelete={false}
              followUpQueueActions
              suppressEditButton={recentActionKey != null && Number(recentActionKey) === Number(row.id)}
              onFollowUpDone={() => onDone(row)}
              onFollowUpCancel={() => onCancelled(row)}
              showToast={showToast}
              onRefresh={onRefresh}
              onOutcomeChange={(patch) => onPatchRow?.(row.id, patch)}
              variant="row"
            />
            </div>
          </div>
        )}
      </div>
      {expanded ? (
        <CustomerCallHistorySplit
          customerId={row.customer_id}
          phone={row.phone_e164}
          callSummary={row.call_summary ?? null}
          className={s.refollowExpandHistory}
        />
      ) : null}
    </article>
  );
}
